import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Heart, Library as LibraryIcon, Music, Sparkles, Clock, Music2 } from 'lucide-react';

const Library = ({ playSong, songs, playlists, likedSongs, toggleLike, user, vibes }) => {
  const [filter, setFilter] = useState('all');
  const [userPlaylists, setUserPlaylists] = useState([]);
  const [madeForYou, setMadeForYou] = useState([]);
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  
  const likedSongsArray = songs.filter(song => likedSongs.has(song.id));

  const filterOptions = [
    { id: 'all', label: 'All' },
    { id: 'playlists', label: 'Playlists' },
    { id: 'recommendations', label: 'For You' }
  ];

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user]);

  const fetchUserData = async () => {
    try {
      // Fetch user's playlists
      const playlistsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/playlists/me`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );
      if (playlistsResponse.ok) {
        const playlistsData = await playlistsResponse.json();
        setUserPlaylists(playlistsData);
      }

      // Fetch Made For You recommendations
      const recommendationsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/recommendations/made-for-you`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );
      if (recommendationsResponse.ok) {
        const recData = await recommendationsResponse.json();
        setMadeForYou(recData);
      }
      setLoadingRecommendations(false);

      // Fetch listening history (need more data for analysis)
      const historyResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/listening-history/recent?limit=100`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        // Set only first 8 for display
        setRecentlyPlayed(historyData.slice(0, 8));
        
        // Use full history data to calculate favorite vibes and genres
        calculateFavoriteVibesAndGenres(historyData);
      }
      setLoadingHistory(false);
    } catch (error) {
      console.error('Failed to fetch library data:', error);
      setLoadingRecommendations(false);
      setLoadingHistory(false);
    }
  };

  // Calculate user's favorite vibes based on actual listening history
  const [favoriteVibes, setFavoriteVibes] = React.useState([]);
  const [favoriteGenres, setFavoriteGenres] = React.useState([]);

  const calculateFavoriteVibesAndGenres = (historyData) => {
    if (!historyData || historyData.length === 0) {
      setFavoriteVibes([]);
      setFavoriteGenres([]);
      return;
    }

    // Count vibes from actually played songs
    const vibeCount = {};
    historyData.forEach(song => {
      if (song.vibes && Array.isArray(song.vibes)) {
        song.vibes.forEach(vibeId => {
          vibeCount[vibeId] = (vibeCount[vibeId] || 0) + 1;
        });
      }
    });
    
    // Get top 4 vibes with their play counts
    const sortedVibes = Object.entries(vibeCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 4)
      .map(([vibeId, playCount]) => {
        const vibe = vibes.find(v => v.id === vibeId);
        return vibe ? { ...vibe, playCount } : null;
      })
      .filter(Boolean);
    
    setFavoriteVibes(sortedVibes);

    // Count genres from actually played songs
    const genreCount = {};
    historyData.forEach(song => {
      if (song.genre) {
        genreCount[song.genre] = (genreCount[song.genre] || 0) + 1;
      }
    });
    
    // Get top 4 genres with their play counts
    const sortedGenres = Object.entries(genreCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 4)
      .map(([genre, count]) => ({ genre, count }));
    
    setFavoriteGenres(sortedGenres);
  };

  const handlePlayLikedSongs = () => {
    if (likedSongsArray.length > 0) {
      playSong(likedSongsArray[0], likedSongsArray);
    }
  };

  return (
    <div className="page-content" style={{ paddingBottom: '100px', minHeight: '100vh' }}>
      <div className="library-header">
        <div className="library-title">
          <LibraryIcon size={24} />
          <h2>Your Library</h2>
        </div>
        
        <div className="library-filters">
          {filterOptions.map(option => (
            <button
              key={option.id}
              className={`filter-btn ${filter === option.id ? 'active' : ''}`}
              onClick={() => setFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="library-content">
        {/* Quick Access Section */}
        <div className="library-section">
          <h3 className="section-title">Quick Access</h3>
          <div className="library-items">
            {/* Liked Songs */}
            <Link to="/liked" className="library-item liked-songs-item" style={{ textDecoration: 'none' }}>
              <div className="library-item-image liked-songs-gradient">
                <Heart size={32} fill="white" color="white" />
              </div>
              <div className="library-item-info">
                <h3>Liked Songs</h3>
                <p>{likedSongsArray.length} songs</p>
              </div>
              <button 
                className="library-item-play"
                onClick={(e) => {
                  e.preventDefault();
                  handlePlayLikedSongs();
                }}
                disabled={likedSongsArray.length === 0}
              >
                <Play size={20} fill="white" />
              </button>
            </Link>
          </div>
        </div>

        {/* Your Playlists */}
        {(filter === 'all' || filter === 'playlists') && userPlaylists.length > 0 && (
          <div className="library-section">
            <h3 className="section-title">Your Playlists</h3>
            <div className="library-items">
              {userPlaylists.map(playlist => (
                <Link key={playlist.id} to={`/playlist/${playlist.id}`} className="library-item" style={{ textDecoration: 'none' }}>
                  <div className="library-item-image" style={{
                    background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Music size={32} color="white" />
                  </div>
                  <div className="library-item-info">
                    <h4>{playlist.name}</h4>
                    <p>Playlist • {playlist.song_ids?.length || 0} songs</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Made For You */}
        {(filter === 'all' || filter === 'recommendations') && user && !loadingRecommendations && madeForYou.length > 0 && (
          <div className="library-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={20} color="#FFD700" />
                <h3 className="section-title" style={{ margin: 0 }}>Made For You</h3>
              </div>
              <Link to="/made-for-you" style={{ color: '#9D4EDD', fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}>
                View All
              </Link>
            </div>
            <div className="library-items">
              {madeForYou.slice(0, 4).map(song => (
                <div key={song.id} className="library-item" onClick={() => playSong(song, madeForYou)} style={{ cursor: 'pointer' }}>
                  <div className="library-item-image">
                    <img src={song.albumArt || song.album_art_url} alt={song.title} />
                  </div>
                  <div className="library-item-info">
                    <h4>{song.title}</h4>
                    <p>Song • {song.artist}</p>
                  </div>
                  <button 
                    className="library-item-play"
                    onClick={(e) => {
                      e.stopPropagation();
                      playSong(song, madeForYou);
                    }}
                  >
                    <Play size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recently Played */}
        {(filter === 'all' || filter === 'recommendations') && !loadingHistory && recentlyPlayed.length > 0 && (
          <div className="library-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Clock size={20} color="#9D4EDD" />
                <h3 className="section-title" style={{ margin: 0 }}>Recently Played</h3>
              </div>
              <Link to="/history" style={{ color: '#9D4EDD', fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}>
                View All
              </Link>
            </div>
            <div className="library-items">
              {recentlyPlayed.map(song => (
                <div key={song.id} className="library-item" onClick={() => playSong(song, recentlyPlayed)} style={{ cursor: 'pointer' }}>
                  <div className="library-item-image">
                    <img src={song.albumArt || song.album_art_url} alt={song.title} />
                  </div>
                  <div className="library-item-info">
                    <h4>{song.title}</h4>
                    <p>Song • {song.artist}</p>
                  </div>
                  <button 
                    className="library-item-play"
                    onClick={(e) => {
                      e.stopPropagation();
                      playSong(song, recentlyPlayed);
                    }}
                  >
                    <Play size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Vibes You Follow */}
        {(filter === 'all' || filter === 'recommendations') && favoriteVibes.length > 0 && (
          <div className="library-section">
            <h3 className="section-title">Your Top Vibes</h3>
            <div className="library-items">
              {favoriteVibes.map(vibe => (
                <Link key={vibe.id} to={`/vibe/${vibe.id}`} className="library-item" style={{ textDecoration: 'none' }}>
                  <div 
                    className="library-item-image"
                    style={{
                      backgroundColor: vibe.color,
                      backgroundImage: vibe.image_url ? `url(${vibe.image_url})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      position: 'relative'
                    }}
                  >
                    {vibe.image_url && (
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)'
                      }} />
                    )}
                  </div>
                  <div className="library-item-info">
                    <h4>{vibe.name}</h4>
                    <p>You played {vibe.playCount} {vibe.playCount === 1 ? 'song' : 'songs'}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Genres You Like */}
        {(filter === 'all' || filter === 'recommendations') && favoriteGenres.length > 0 && (
          <div className="library-section">
            <h3 className="section-title">Your Top Genres</h3>
            <div className="library-items">
              {favoriteGenres.map(({ genre, count }) => {
                const genreSlug = genre.toLowerCase().replace(/\s+/g, '-');
                return (
                  <Link key={genre} to={`/genre/${genreSlug}`} className="library-item" style={{ textDecoration: 'none' }}>
                    <div 
                      className="library-item-image"
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Music2 size={32} color="white" />
                    </div>
                    <div className="library-item-info">
                      <h4>{genre}</h4>
                      <p>You played {count} {count === 1 ? 'time' : 'times'}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Library;